import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.ts';
import { Button, Icons, Spinner, cx } from '../../components/ui.tsx';
import { BRAND, BrandIcon } from '../../components/icons.tsx';
import { formatDateTime } from '../../lib/display.ts';
import type { Folder, Page, DriveFile } from '../../lib/types.ts';
import type { SpreadsheetSummary } from '../../lib/sheet-queries.ts';

/**
 * Choose where a new sheet is saved.
 *
 * A browsable tree rather than a folder dropdown, and it shows the files and
 * sheets already in each folder. That is the difference between picking a
 * path and seeing where you are putting something: the client's folders are
 * named after customers and months, and "is this the right SHAHEEN folder"
 * is answered by what is already in it, not by its name.
 *
 * Existing names are listed for a second reason — the server rejects a
 * duplicate name in a folder, and finding that out before typing beats
 * finding out on submit.
 */

export interface SaveTarget {
  name: string;
  folderId: string | null;
}

export function SavePicker({
  title,
  subtitle,
  initialName,
  initialFolderId,
  confirmLabel,
  askForName = true,
  onConfirm,
  onClose,
}: {
  title: string;
  /** Replaces "Choose where it is saved." where a different act needs saying. */
  subtitle?: string | undefined;
  initialName: string;
  initialFolderId: string | null;
  confirmLabel: string;
  /**
   * Whether the thing being placed is also being named.
   *
   * A new sheet is. Files already have their names — a dozen of them — so
   * uploading asks only where, and the name field would be a box with nothing
   * useful to put in it.
   */
  askForName?: boolean | undefined;
  onConfirm(target: SaveTarget): void;
  onClose(): void;
}) {
  const [folderId, setFolderId] = useState<string | null>(initialFolderId);
  const [name, setName] = useState(initialName);
  const [error, setError] = useState<string | null>(null);

  const folders = useQuery({
    queryKey: ['folders', folderId],
    queryFn: () =>
      api.get<Folder[]>(`/api/drive/folders${folderId ? `?parentId=${folderId}` : ''}`),
  });

  const files = useQuery({
    queryKey: ['files', folderId],
    queryFn: () =>
      api.get<Page<DriveFile>>(`/api/drive/files${folderId ? `?folderId=${folderId}` : ''}`),
  });

  const sheets = useQuery({
    queryKey: ['sheets', 'folder', folderId],
    queryFn: () =>
      api.get<SpreadsheetSummary[]>(`/api/sheets${folderId ? `?folderId=${folderId}` : ''}`),
  });

  const crumbs = useQuery({
    queryKey: ['breadcrumbs', folderId ?? 'root'],
    queryFn: () =>
      api.get<Array<{ id: string; name: string }>>(`/api/drive/folders/${folderId}/breadcrumbs`),
    enabled: folderId !== null,
  });

  const takenNames = useMemo(
    () =>
      new Set([
        ...(sheets.data ?? []).map((sheet) => sheet.name.toLowerCase()),
        // A sheet and a file can share a folder but the server's uniqueness
        // is per-kind, so only sheets actually clash. Files are listed for
        // orientation, not validation.
      ]),
    [sheets.data],
  );

  const clash = askForName && takenNames.has(name.trim().toLowerCase());

  const submit = useCallback(() => {
    const trimmed = name.trim();
    if (askForName && !trimmed) {
      setError('Give the sheet a name.');
      return;
    }
    if (clash) {
      setError(`This folder already has a sheet called "${trimmed}".`);
      return;
    }
    onConfirm({ name: trimmed, folderId });
    onClose();
  }, [askForName, clash, folderId, name, onClose, onConfirm]);

  useEffect(() => setError(null), [folderId, name]);

  const loading = folders.isLoading || files.isLoading || sheets.isLoading;
  const path = folderId ? (crumbs.data ?? []).map((crumb) => crumb.name).join(' / ') : 'My Drive';

  return (
    <div>
      <div className="border-b border-border px-5 pb-3 pt-5">
        <h2 className="text-h3 text-ink">{title}</h2>
        <p className="mt-1 text-body-sm text-body">{subtitle ?? 'Choose where it is saved.'}</p>
      </div>

      {/* Breadcrumbs, so the current location is always visible and one click
          from anywhere above it. */}
      <div className="flex flex-wrap items-center gap-1 border-b border-border px-5 py-2 text-meta">
        <button
          type="button"
          onClick={() => setFolderId(null)}
          className={cx(
            'rounded px-1.5 py-0.5 transition-fast',
            folderId === null ? 'font-medium text-ink' : 'text-blue-700 hover:bg-sunken',
          )}
        >
          My Drive
        </button>
        {(crumbs.data ?? []).map((crumb, index, all) => (
          <span key={crumb.id} className="flex items-center gap-1">
            <span className="text-subtle" aria-hidden>
              ›
            </span>
            <button
              type="button"
              onClick={() => setFolderId(crumb.id)}
              className={cx(
                'rounded px-1.5 py-0.5 transition-fast',
                index === all.length - 1 ? 'font-medium text-ink' : 'text-blue-700 hover:bg-sunken',
              )}
            >
              {crumb.name}
            </button>
          </span>
        ))}
      </div>

      <div className="h-72 overflow-y-auto px-2 py-1">
        {loading ? (
          <div className="flex items-center gap-2 px-3 py-8 text-body-sm text-muted">
            <Spinner /> Loading…
          </div>
        ) : (
          <>
            {(folders.data ?? []).map((folder) => (
              <button
                key={folder.id}
                type="button"
                onDoubleClick={() => setFolderId(folder.id)}
                onClick={() => setFolderId(folder.id)}
                className="flex w-full items-center gap-2.5 rounded-md px-3 py-1.5 text-left transition-fast hover:bg-blue-50"
              >
                <span className="text-blue-600" aria-hidden>
                  <BrandIcon src={BRAND.folder} size={18} />
                </span>
                <span className="flex-1 truncate text-body-sm text-ink">{folder.name}</span>
                <span className="text-micro text-subtle">Open</span>
              </button>
            ))}

            {/* Existing contents, greyed: they are context, not destinations. */}
            {(sheets.data ?? []).map((sheet) => (
              <div
                key={sheet.id}
                className="flex items-center gap-2.5 rounded-md px-3 py-1.5 text-left"
              >
                <span className="text-[var(--success)]" aria-hidden>
                  <GridGlyph />
                </span>
                <span className="flex-1 truncate text-body-sm text-muted">{sheet.name}</span>
                <span className="text-micro text-subtle">{formatDateTime(sheet.updatedAt)}</span>
              </div>
            ))}

            {(files.data?.items ?? []).map((file) => (
              <div
                key={file.id}
                className="flex items-center gap-2.5 rounded-md px-3 py-1.5 text-left"
              >
                <span className="text-subtle" aria-hidden>
                  <Icons.File />
                </span>
                <span className="flex-1 truncate text-body-sm text-muted">{file.name}</span>
              </div>
            ))}

            {(folders.data ?? []).length === 0 &&
            (sheets.data ?? []).length === 0 &&
            (files.data?.items ?? []).length === 0 ? (
              <p className="px-3 py-8 text-center text-body-sm text-muted">This folder is empty.</p>
            ) : null}
          </>
        )}
      </div>

      <div className="border-t border-border px-5 py-3">
        <label
          className={cx('block text-meta font-medium text-body', askForName ? '' : 'hidden')}
          htmlFor="save-name"
        >
          File name
        </label>
        <input
          hidden={!askForName}
          id="save-name"
          autoFocus={askForName}
          value={name}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              submit();
            }
          }}
          className={cx(
            'mt-1.5 h-control w-full rounded-md border bg-surface px-3 text-body-sm text-ink',
            error || clash ? 'border-[var(--danger)]' : 'border-border-input',
          )}
          aria-invalid={Boolean(error || clash)}
        />

        <p className={cx('text-micro text-muted', askForName ? 'mt-1.5' : '')}>
          {askForName ? 'Saving to' : 'Uploading to'}{' '}
          <span className="font-medium text-ink">{path}</span>
        </p>

        {error || clash ? (
          <p className="mt-1 text-micro text-[var(--danger)]">
            {error ?? `This folder already has a sheet called "${name.trim()}".`}
          </p>
        ) : null}
      </div>

      <div className="flex justify-end gap-2 border-t border-border px-5 py-3">
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={clash}>
          {confirmLabel}
        </Button>
      </div>
    </div>
  );
}

function GridGlyph() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
    >
      <rect x="2.5" y="2.5" width="13" height="13" rx="1.5" />
      <path d="M2.5 6.5h13M2.5 10.5h13M7 6.5v9M11.5 6.5v9" />
    </svg>
  );
}
